import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail, validatePhoneNumber } from '../../../util';
import { useAppContext } from '../../../context/AppContext';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import './AddClientModal.css'
import { getFullDate } from '../../../util';
import { capitalizeFirst, sanitizeUrl } from '../../../utils/functions';
import AuthenticatedImage from '../../AuthenticatedImage/AuthenticatedImage';
import { capitalize } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useNotification } from '../../notifications/useNotification';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import TourButton from '../../TourButton/TourButton';
import { getAddClientModalSteps } from '../../../tours/addClientModal';
import { FaCamera } from 'react-icons/fa';

/**
 * Renders the modal to add a new client.
 *
 * @param {Object} props
 * @param {function} props.setShowModal if true shows the modal else it hides the modal
 * @param {function} props.onSave the function to call after we save the client
 * @param {Object} props.client if the client is passed it means we are editing the client and not creating a new one
*/
const AddClientModal = ({ setShowModal, onSave, client }) => {
    const { t } = useTranslation();
    const { currentLanguage } = useAppContext();
    const { showSuccess, showError } = useNotification();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [loading, setLoading] = useState(false)
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState(sanitizeUrl(client?.profilePhotoUrl) || null);
    const fileInputRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [tab, setTab] = useState(0); // 0: personal info; 1: food preferences
    const { appColors } = useAppContext();
    const [likedFoodSearchList, setLikedFoodSearchList] = useState([]); // the list of foods we retrieve from DB when searching for a specific food
    const [likedFoodsList, setLikedFoodsList] = useState(client && client.likedFoods ? client.likedFoods : []); // the list of selected foods
    const [dislikedFoodSearchList, setDislikedFoodSearchList] = useState([]); // the list of foods we retrieve from DB when searching for a specific food
    const [dislikedFoodsList, setDislikedFoodsList] = useState(client && client.dislikedFoods ? client.dislikedFoods : []);  // the list of selected foods
    const [foodIntolerancesSearchList, setFoodIntolerancesSearchList] = useState([]); // the list of foods we retrieve from DB when searching for a specific food
    const [foodIntolerancesList, setFoodIntolerancesList] = useState(client && client.intolerances ? client.intolerances : []);  // the list of selected foods
    const [illnessSearchList, setIllnessSearchList] = useState([]); // the list of illnesses we retrieve from DB when searching
    const [illnessesList, setIllnessesList] = useState(client && client.illnesses ? client.illnesses : []);  // the list of selected illnesses
    const [onlyGenerics, setOnlyGenerics] = useState(true); // filter food search to generic brand only
    const [formData, setFormData] = useState({
        name: client && client.name ? client.name : '',
        surname: client && client.surname ? client.surname : '',
        birthDate: client && client.birthDate ? getFullDate(client.birthDate) : '',
        job: client && client.job ? client.job : '',
        email: client && client.email ? client.email : '',
        phone: client && client.phone ? client.phone : '',
        gender: client && client.gender ? client.gender : ''
    });
    const { dietist } = useAppContext();

    const tourSteps = useMemo(() => getAddClientModalSteps(t), [t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setProfilePhoto(file);
        setProfilePhotoPreview(URL.createObjectURL(file));
    };

    const getIllnessesFilteredByName = async (illnessName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/illnesses/?nameFilter=${encodeURIComponent(illnessName)}`;
        url += `&language=${encodeURIComponent(currentLanguage)}`;
        let response = await axios.get(url, { withCredentials: true });
        return response.data;
    }

    const handleIllnessChange = async (e) => {
        const illnessName = e.target.value;
        let result = await getIllnessesFilteredByName(illnessName);
        setIllnessSearchList(result);
    }

    const handleFoodIntoleranceChange = async (e) => {
        const foodIntoleranceName = e.target.value;
        let result = await getFoodIntolerancesFilteredByName(foodIntoleranceName);
        setFoodIntolerancesSearchList(result);
    }

    const getFoodIntolerancesFilteredByName = async (foodIntoleranceName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/food-intolerances/?nameFilter=${encodeURIComponent(foodIntoleranceName)}`;
        url += `&language=${encodeURIComponent(currentLanguage)}`;
        let response = await axios.get(url, { withCredentials: true });
        return response.data;
    }

    const handleFoodNameChange = async (e) => {
        const foodName = e.target.value;
        const componentName = e.target.name; // the component name that called the function
        let result = await getFoodsFilteredByName(foodName);
        switch (componentName) {
            case "liked-foods":
                setLikedFoodSearchList(result);
                break;
            case "disliked-foods":
                setDislikedFoodSearchList(result);
                break;
            default:
                console.log("Error when selecting foods")
        }
    }

    const getFoodsFilteredByName = async (foodName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${encodeURIComponent(foodName)}`;
        url += `&language=${encodeURIComponent(currentLanguage)}`;
        if (onlyGenerics) url += '&onlyGenerics=true';
        let response = await axios.get(url, { withCredentials: true });
        return response.data;
    }

    const validateForm = () => {
        const newErrors = {};
        // Name
        if (!formData.name)
            newErrors.name = t("errorMessages.nameRequired");
        else if (formData.name.length > 50)
            newErrors.name = t("errorMessages.too-long");
        // Surname
        if (!formData.surname)
            newErrors.surname = t("errorMessages.surnameRequired");
        else if (formData.surname.length > 50)
            newErrors.surname = t("errorMessages.too-long");
        // Birth Date
        if (new Date(formData.birthDate) > new Date())
            newErrors.birthDate = t("errorMessages.dateCantBeGreaterThanToday");
        // Email
        /*
        if (!formData.email)
            newErrors.email = t("errorMessages.emailRequired");
        */
        if (formData.email && formData.email.length > 100)
            newErrors.email = t("errorMessages.too-long");
        else if (formData.email && !validateEmail(formData.email))
            newErrors.email = t("errorMessages.emailInvalid");
        // Phone
        if (formData.phone && !validatePhoneNumber(formData.phone))
            newErrors.phone = t("errorMessages.invalid-phone");
        // Gender
        if (!formData.gender)
            newErrors.gender = t("errorMessages.gender-required");
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = async () => {
        if (validateForm()) {
            try {
                setLoading(true);
                const dataToSend = new FormData();
                Object.entries(formData).forEach(([key, value]) => dataToSend.append(key, value));
                dataToSend.append('likedFoods', JSON.stringify(likedFoodsList));
                dataToSend.append('dislikedFoods', JSON.stringify(dislikedFoodsList));
                dataToSend.append('intolerances', JSON.stringify(foodIntolerancesList));
                dataToSend.append('illnesses', JSON.stringify(illnessesList));
                dataToSend.append('dietist', dietist._id);
                if (profilePhoto) dataToSend.append('profilePhoto', profilePhoto);

                // if the client was passed => it's an edit
                if (client) {
                    await axios.put(`${process.env.REACT_APP_BACKEND_URL}/clients/${client._id}`, dataToSend, { withCredentials: true });
                } else {
                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/clients`, dataToSend, { withCredentials: true });
                }

                await onSave();

                if (client)
                    showSuccess(capitalizeFirst(t("client-data-edited")))
                else
                    showSuccess(capitalizeFirst(t("client-created")))
                handleClose();
            } catch (error) {
                let errorMessage = ""
                if (client)
                    errorMessage = "client-not-edited"
                else
                    errorMessage = "client-not-created"
                showError(capitalizeFirst(t("error")) + ": " + capitalizeFirst(t(errorMessage)))
                console.error('Add client modal error:', error);
            } finally {
                setLoading(false);
            }
        }
    }

    const handleClose = () => {
        setFormData({
            name: '',
            surname: '',
            birthDate: '',
            job: '',
            email: '',
            phone: '',
            gender: ''
        });
        setLikedFoodSearchList([]);
        setLikedFoodsList([]);
        setDislikedFoodSearchList([]);
        setDislikedFoodsList([]);
        setFoodIntolerancesSearchList([]);
        setFoodIntolerancesList([]);
        setIllnessSearchList([]);
        setIllnessesList([]);
        setProfilePhoto(null);
        setProfilePhotoPreview(null);
        setShowModal(false);
    }

    return (
        <>
        <LoadingOverlay open={loading} />
        <div className="acm-overlay">
            <div className="acm-modal" data-tour="acm-modal">

                {/* ── Header ── */}
                <div className="acm-header">
                    <div className="acm-header-text">
                        <h2 className="acm-title">
                            {client ? t('edit-client') : t('add-new-client')}
                        </h2>
                    </div>
                    <div className="acm-tutorial-btn">
                        <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                    </div>
                    <button className="acm-close-btn" onClick={handleClose} aria-label="Close">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* ── Tabs ── */}
                <div className="acm-tabs-wrap" data-tour="acm-tabs">
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        TabIndicatorProps={{ style: { backgroundColor: appColors.secondary } }}
                    >
                        <Tab
                            label={<span style={{ color: tab === 0 ? appColors.secondary : '#475569' }}>{capitalizeFirst(t("personal"))}</span>}
                            value={0}
                        />
                        <Tab
                            label={<span style={{ color: tab === 1 ? appColors.secondary : '#475569' }}>{t("Food")}</span>}
                            value={1}
                        />
                        <Tab
                            label={<span style={{ color: tab === 2 ? appColors.secondary : '#475569' }}>{capitalizeFirst(t("health"))}</span>}
                            value={2}
                        />
                    </Tabs>
                </div>

                {/* ── Body ── */}
                <div className="acm-body">
                    {tab === 0 &&
                    <>
                        {/* Photo */}
                        <div className="acm-photo-wrap" data-tour="acm-photo" onClick={() => fileInputRef.current.click()}>
                            <AuthenticatedImage
                                src={profilePhotoPreview}
                                fallbackSrc='/images/anonymous_avatar_icon.jpg'
                                alt='profile'
                                className="acm-photo-img"
                            />
                            <div className="acm-photo-overlay">
                                <FaCamera />
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept="image/*"
                                onChange={handlePhotoChange}
                            />
                        </div>

                        {/* Name + Surname */}
                        <div className="acm-row" data-tour="acm-name">
                            <div className="acm-field">
                                <label className="acm-label">{t("Name")}</label>
                                <input
                                    className="acm-input"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t("enter-name")}
                                />
                                {errors.name && <span className="acm-error">{errors.name}</span>}
                            </div>
                            <div className="acm-field">
                                <label className="acm-label">{t("Surname")}</label>
                                <input
                                    className="acm-input"
                                    type="text"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={handleInputChange}
                                    placeholder={t("enter-surname")}
                                />
                                {errors.surname && <span className="acm-error">{errors.surname}</span>}
                            </div>
                        </div>

                        {/* Birth Date + Gender */}
                        <div className="acm-row" data-tour="acm-birthdate">
                            <div className="acm-field">
                                <label className="acm-label">{t("birth-date")}</label>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={formData.birthDate ? dayjs(formData.birthDate) : null}
                                        onChange={(newValue) => {
                                            setFormData({
                                                ...formData,
                                                birthDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                                            });
                                        }}
                                        format="DD/MM/YYYY"
                                        maxDate={dayjs()}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: {
                                                    width: '100%',
                                                    '& .MuiInputBase-root': {
                                                        height: '38px',
                                                        borderRadius: '8px',
                                                        fontSize: '0.875rem',
                                                        fontFamily: "'Public Sans', sans-serif",
                                                        color: '#0f172a',
                                                        backgroundColor: '#ffffff',
                                                    },
                                                    '& .MuiOutlinedInput-input': { padding: '0 12px' },
                                                    '& fieldset': { borderColor: '#cbd5e1' },
                                                    '&:hover fieldset': { borderColor: '#fc9f5b !important' },
                                                    '& .Mui-focused fieldset': { borderColor: '#fd4701 !important', boxShadow: '0 0 0 3px rgba(253,71,1,0.1)' },
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                {errors.birthDate && <span className="acm-error">{errors.birthDate}</span>}
                            </div>
                            <div className="acm-field">
                                <label className="acm-label">{t("Gender")}</label>
                                <select
                                    className="acm-select"
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                >
                                    <option value="">{t("Gender")}</option>
                                    <option value="male">{capitalize(t("male"))}</option>
                                    <option value="female">{capitalize(t("female"))}</option>
                                </select>
                                {errors.gender && <span className="acm-error">{errors.gender}</span>}
                            </div>
                        </div>

                        {/* Job */}
                        <div className="acm-field">
                            <label className="acm-label">{t("Job")}</label>
                            <input
                                className="acm-input"
                                type="text"
                                name="job"
                                value={formData.job}
                                onChange={handleInputChange}
                                placeholder={t("enter-job")}
                            />
                            {errors.job && <span className="acm-error">{errors.job}</span>}
                        </div>

                        {/* Email + Phone */}
                        <div className="acm-row" data-tour="acm-contact">
                            <div className="acm-field">
                                <label className="acm-label">{t("Email")}</label>
                                <input
                                    className="acm-input"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder={t("enter-email")}
                                />
                                {errors.email && <span className="acm-error">{errors.email}</span>}
                            </div>
                            <div className="acm-field">
                                <label className="acm-label">{t("phone-number")}</label>
                                <input
                                    className="acm-input"
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder={t("enter-phone")}
                                />
                                {errors.phone && <span className="acm-error">{errors.phone}</span>}
                            </div>
                        </div>
                    </>
                    }

                    {tab === 1 &&
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Tooltip
                                title={t('only-generic-brand-info')}
                                arrow
                                placement="top"
                            >
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#94a3b8', mr: 0.5, cursor: 'default' }} />
                            </Tooltip>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={onlyGenerics}
                                        onChange={(e) => {
                                            setOnlyGenerics(e.target.checked);
                                            setLikedFoodSearchList([]);
                                            setDislikedFoodSearchList([]);
                                        }}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: appColors.secondary },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: appColors.secondary },
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>{t('only-generic-brand')}</span>}
                                sx={{ m: 0 }}
                            />
                        </Box>

                        <div className="acm-field">
                            <label className="acm-label">{t("liked-foods")}</label>
                            <div className="acm-multi-select-wrap">
                                <Autocomplete
                                    id="liked-food-search-bar"
                                    name="liked-food-search-bar"
                                    multiple
                                    freeSolo
                                    value={likedFoodsList}
                                    onChange={(event, newValue) => setLikedFoodsList(newValue)}
                                    options={likedFoodSearchList}
                                    getOptionLabel={(food) => {
                                        if (typeof food === 'string') return food;
                                        const name = food.name?.[currentLanguage] || '';
                                        return food.brand ? `${name} - ${food.brand}` : name;
                                    }}
                                    renderOption={(props, option) => {
                                        const name = option.name?.[currentLanguage] || '';
                                        const brand = option.brand;
                                        const brandDisplay = brand
                                            ? (typeof brand === 'object'
                                                ? (brand?.[currentLanguage] ?? brand?.en ?? '')
                                                : brand)
                                            : null;
                                        return (
                                            <li {...props} key={option._id}>
                                                {name}
                                                {brandDisplay && (
                                                    <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                        {brandDisplay}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            onChange={handleFoodNameChange}
                                            placeholder={t("select-food")}
                                            name='liked-foods'
                                        />
                                    )}
                                    sx={{
                                        '.MuiInputBase-root': { height: 140, maxHeight: 140, overflowY: 'auto' },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'transparent' },
                                            '&:hover fieldset': { borderColor: 'transparent' },
                                            '&.Mui-focused fieldset': { borderColor: 'transparent' },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className="acm-field">
                            <label className="acm-label">{t("disliked-foods")}</label>
                            <div className="acm-multi-select-wrap">
                                <Autocomplete
                                    id="disliked-food-search-bar"
                                    name="disliked-food-search-bar"
                                    multiple
                                    freeSolo
                                    value={dislikedFoodsList}
                                    onChange={(event, newValue) => setDislikedFoodsList(newValue)}
                                    options={dislikedFoodSearchList}
                                    getOptionLabel={(food) => {
                                        if (typeof food === 'string') return food;
                                        const name = food.name?.[currentLanguage] || '';
                                        return food.brand ? `${name} - ${food.brand}` : name;
                                    }}
                                    renderOption={(props, option) => {
                                        const name = option.name?.[currentLanguage] || '';
                                        const brand = option.brand;
                                        const brandDisplay = brand
                                            ? (typeof brand === 'object'
                                                ? (brand?.[currentLanguage] ?? brand?.en ?? '')
                                                : brand)
                                            : null;
                                        return (
                                            <li {...props} key={option._id}>
                                                {name}
                                                {brandDisplay && (
                                                    <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                        {brandDisplay}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            onChange={handleFoodNameChange}
                                            placeholder={t("select-food")}
                                            name='disliked-foods'
                                        />
                                    )}
                                    sx={{
                                        '.MuiInputBase-root': { height: 140, maxHeight: 140, overflowY: 'auto' },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'transparent' },
                                            '&:hover fieldset': { borderColor: 'transparent' },
                                            '&.Mui-focused fieldset': { borderColor: 'transparent' },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </>
                    }

                    {tab === 2 &&
                    <>
                        <div className="acm-field">
                            <label className="acm-label">{capitalizeFirst(t("intolerances"))}</label>
                            <div className="acm-multi-select-wrap">
                                <Autocomplete
                                    id="food-intolerances-search-bar"
                                    name="food-intolerances-search-bar"
                                    multiple
                                    freeSolo
                                    value={foodIntolerancesList}
                                    onChange={(event, newValue) => setFoodIntolerancesList(newValue)}
                                    options={foodIntolerancesSearchList.map((intolerance) => intolerance)}
                                    getOptionLabel={(intolerance) => intolerance.name[currentLanguage]}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            onChange={handleFoodIntoleranceChange}
                                            placeholder={t("select-food-intolerance")}
                                            name='food-intolerance'
                                        />
                                    )}
                                    sx={{
                                        '.MuiInputBase-root': { height: 140, maxHeight: 140, overflowY: 'auto' },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'transparent' },
                                            '&:hover fieldset': { borderColor: 'transparent' },
                                            '&.Mui-focused fieldset': { borderColor: 'transparent' },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        <div className="acm-field">
                            <label className="acm-label">{capitalizeFirst(t("illnesses"))}</label>
                            <div className="acm-multi-select-wrap">
                                <Autocomplete
                                    id="illnesses-search-bar"
                                    name="illnesses-search-bar"
                                    multiple
                                    value={illnessesList}
                                    onChange={(event, newValue) => setIllnessesList(newValue)}
                                    options={illnessSearchList}
                                    getOptionLabel={(illness) => illness.name?.[currentLanguage] ?? ''}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            onChange={handleIllnessChange}
                                            placeholder={t("select-illness")}
                                            name='illness'
                                        />
                                    )}
                                    sx={{
                                        '.MuiInputBase-root': { height: 140, maxHeight: 140, overflowY: 'auto' },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'transparent' },
                                            '&:hover fieldset': { borderColor: 'transparent' },
                                            '&.Mui-focused fieldset': { borderColor: 'transparent' },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </>
                    }
                </div>

                {/* ── Footer ── */}
                <div className="acm-footer" data-tour="acm-footer">
                    <button className="acm-btn acm-btn-cancel" onClick={handleClose}>
                        {t('Cancel')}
                    </button>
                    <button className="acm-btn acm-btn-save" onClick={handleSubmit}>
                        {t('Save')}
                    </button>
                </div>

            </div>
        </div>
        </>
    );
};

export default AddClientModal;
